import mapboxgl, { type MapEventOf, type MapEventType } from 'mapbox-gl'

type MapEvents = Partial<
  Record<
    MapEventType,
    {
      timeout: ReturnType<typeof setTimeout> | null
      listeners: Record<string, (event: unknown) => void>
    }
  >
>
const current = new Map<mapboxgl.Map, MapEvents>()

export default function addUpperLayerEventListener<EventName extends MapEventType>(
  map: mapboxgl.Map,
  eventName: EventName,
  layersIds: string[],
  listener: (event: MapEventOf<EventName>) => void,
) {
  const handleEvent = (event: MapEventOf<EventName>) => {
    const currentMapEvents = current.get(map)
    const mapEvents: MapEvents = currentMapEvents ?? {}
    if (!currentMapEvents) {
      current.set(map, mapEvents)
    }

    const eventEntry: {
      timeout: ReturnType<typeof setTimeout> | null
      listeners: Record<string, (event: unknown) => void>
    } = mapEvents[eventName] ?? { timeout: null, listeners: {} }
    mapEvents[eventName] = eventEntry

    if (eventEntry.timeout !== null) {
      clearTimeout(eventEntry.timeout)
    }

    eventEntry.listeners[layersIds[0]] = listener as (event: unknown) => void
    eventEntry.timeout = setTimeout(() => {
      const listeners = eventEntry.listeners
      delete mapEvents[eventName]
      if (Object.keys(mapEvents).length === 0) {
        current.delete(map)
      }

      const layers = map.getStyle().layers?.map(layer => layer.id) ?? []
      const listenersKeys = Object.keys(listeners)
      const indexes = listenersKeys.map(key => layers.indexOf(key))
      const maxIndex = Math.max(...indexes)

      const selectedLayer = layers[maxIndex]
      const selectedListener = listeners[selectedLayer]

      if (selectedListener) selectedListener(event)
    }, 200)
  }

  map.on(eventName, layersIds, handleEvent)

  return () => {
    map.off(eventName, layersIds, handleEvent)
  }
}
